import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Home, Clock } from "lucide-react";
import { API } from "@/lib/checkout";
import { useNoIndex } from "@/hooks/useNoIndex";
import { trackPurchase } from "@/lib/analytics";
import { SiteHeader } from "@/components/Brand";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function ThankYou() {
  useNoIndex();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalToken = params.get("token");
  const [state, setState] = useState("checking");
  const [accessToken, setAccessToken] = useState(null);
  const fired = useRef(false);

  useEffect(() => {
    let cancelled = false;
    // Tracking is fire-and-forget: it must NEVER block access/download. Fires the
    // purchase event only when the backend confirms a real paid order, once per
    // order (server dedupe + client flag inside trackPurchase). transaction_id is
    // the order/capture id, never the access token.
    const confirmAndTrack = async (accessToken) => {
      try {
        const res = await fetch(`${API}/purchase/confirm`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: accessToken }),
        });
        if (!res.ok) return;
        const info = await res.json();
        if (!fired.current && !info.already_tracked) {
          fired.current = true;
          trackPurchase({ value: info.value, currency: info.currency, transaction_id: info.transaction_id });
        }
      } catch (e) {
        /* tracking must never block the buyer */
      }
    };

    // PayPal path: capture already happened client-side; the token is the access
    // token. Decide the page state from the token's VALIDITY (GET /access), not the
    // tracking call. A real order resolves immediately; retry up to 20s to absorb a
    // slow write. Only a definitely-unknown token (server said 404) shows error;
    // if the backend is merely unreachable, show the soft processing state.
    if (paypalToken) {
      (async () => {
        const deadline = Date.now() + 20000;
        let decided = false;
        let saw404 = false;
        while (!cancelled && Date.now() < deadline) {
          let ar = null;
          try {
            ar = await fetch(`${API}/access/${paypalToken}`);
          } catch (e) {
            ar = null;
          }
          if (cancelled) return;
          if (ar && ar.ok) {
            setAccessToken(paypalToken);
            setState("paid");
            confirmAndTrack(paypalToken);
            decided = true;
            break;
          }
          if (ar && ar.status === 410) {
            setState("error");
            decided = true;
            break;
          }
          if (ar && ar.status === 404) saw404 = true;
          setState("checking");
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        }
        if (!cancelled && !decided) setState(saw404 ? "error" : "processing");
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!sessionId) {
      setState("error");
      return;
    }
    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        // Payment still settling (capture/webhook in flight): soft processing state,
        // never a hard error. Access link is emailed on fulfillment.
        setState("processing");
        return;
      }
      try {
        const res = await fetch(`${API}/checkout/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setAccessToken(data.access_token);
          setState("paid");
          if (data.access_token) confirmAndTrack(data.access_token);
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId, paypalToken]);

  return (
    <div className="min-h-screen bg-[#0B132B]" data-testid="thankyou-page">
      <SiteHeader />
      <div className="flex flex-col items-center px-6 py-16">
        <div className="w-full max-w-[560px] rounded-3xl border border-white/10 bg-[#14213D] p-8 md:p-12 text-center shadow-2xl">
          {state === "checking" && (
            <div data-testid="thankyou-checking">
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#F5A623]" />
              <h1 className="font-heading mt-6 text-2xl font-extrabold text-white">Confirming your payment</h1>
              <p className="mt-3 text-[#BFD4E8]">Hang tight for a moment while we verify your purchase.</p>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="thankyou-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-[#2ECC71]" />
              <h1 className="font-heading mt-6 text-3xl font-extrabold text-white">You're In.</h1>
              <p className="mt-3 text-[#BFD4E8]">
                Everything you purchased is ready below. Don't try to set it all
                up today. Open it, do the one smallest thing, done.
              </p>
              {accessToken ? (
                <Link
                  to={`/access/${accessToken}`}
                  data-testid="access-link-button"
                  className="group mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5A623] px-8 py-4 font-heading text-lg font-extrabold text-[#0B132B] transition-transform duration-200 hover:-translate-y-1"
                >
                  Open Your Download Page
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              ) : (
                <p className="mt-6 text-sm text-[#7BA8C4]">We also emailed your access link to your inbox.</p>
              )}
              <p className="mt-5 text-xs text-[#7BA8C4]">
                A copy of your access link has been sent to your email. Questions? support@commandcenteradhd.com
              </p>
              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="text-sm text-[#BFD4E8]">You also have access to the private community.</p>
                <Link
                  to="/community"
                  data-testid="thankyou-community-link"
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-white/5"
                >
                  Join the community
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}

          {state === "processing" && (
            <div data-testid="thankyou-processing">
              <Clock className="mx-auto h-14 w-14 text-[#F5A623]" />
              <h1 className="font-heading mt-6 text-2xl font-extrabold text-white">Your payment is processing</h1>
              <p className="mt-3 text-[#BFD4E8]">
                Your access link is on its way to your email. You can close this page. We will send your link as soon as the payment settles.
              </p>
              <Link
                to="/"
                data-testid="processing-home-button"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-4 font-heading text-base font-bold text-white transition-colors hover:bg-white/10"
              >
                <Home className="h-5 w-5" /> Back to Home
              </Link>
            </div>
          )}

          {(state === "error" || state === "expired") && (
            <div data-testid="thankyou-error">
              <AlertCircle className="mx-auto h-14 w-14 text-[#FF6B6B]" />
              <h1 className="font-heading mt-6 text-2xl font-extrabold text-white">
                {state === "expired" ? "This checkout session expired" : "We could not confirm your payment yet"}
              </h1>
              <p className="mt-3 text-[#BFD4E8]">
                If you were charged, your access link is on its way to your email. Otherwise, please try again from the homepage.
              </p>
              <Link
                to="/"
                data-testid="back-home-button"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-4 font-heading text-base font-bold text-white transition-colors hover:bg-white/10"
              >
                <Home className="h-5 w-5" /> Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
